import React, { FC, useCallback, useMemo } from 'react';
import { Popover, Radio } from 'antd';
import RadioGroupInfo from './RadioGroupInfo';

interface IOWRadioGroup {
	items: any[];
	value?: any;
	valueAccessor: (x: any) => any;
	onChange?: (val: any) => void;
	renderRecord: (x: any) => any;
	info?: any;
	disabled?: boolean;
	className?: any;
}

const OWRadioGroup: FC<IOWRadioGroup> = ({
	items,
	value,
	valueAccessor,
	onChange,
	renderRecord,
	info,
	disabled,
	className,
}): React.ReactElement => {
	const itemsHasUndefinedAsAValue = useMemo(
		() => items.some((_) => valueAccessor(_) === undefined),
		[items]
	);

	const getUndefinedAsStringIfApplicable = useCallback(
		(val) => (itemsHasUndefinedAsAValue && val === undefined ? 'undefined' : val),
		[itemsHasUndefinedAsAValue]
	);

	const modifiedValue = useMemo(
		() => getUndefinedAsStringIfApplicable(value),
		[getUndefinedAsStringIfApplicable, value]
	);

	const onChangeHandler = useCallback(
		(e) => {
			const val = e.target.value;
			const modifiedValue = itemsHasUndefinedAsAValue && val === 'undefined' ? undefined : val;
			onChange(modifiedValue);
		},
		[itemsHasUndefinedAsAValue, onChange]
	);

	const valueAccessorWithUndefinedHandler = useCallback(
		(item) => {
			const valueForItem = valueAccessor(item);
			return getUndefinedAsStringIfApplicable(valueForItem);
		},
		[getUndefinedAsStringIfApplicable, valueAccessor]
	);

	return (
		<div className={`${className ? className : ''} flex flex-row items-center`}>
			<Radio.Group disabled={disabled} onChange={onChangeHandler} value={modifiedValue}>
				{items.map((item, idx) => (
					<Popover
						content={item.disabled && item.disabledMessage ? item.disabledMessage : ''}
						trigger="hover"
						key={`radio-${valueAccessor(item)}-${idx}`}
					>
						<Radio.Button
							style={{ flexWrap: 'nowrap', height: 40, lineHeight: '40px' }}
							value={valueAccessorWithUndefinedHandler(item)}
							disabled={item.disabled}
							type="ghost"
						>
							{renderRecord(item)}
						</Radio.Button>
					</Popover>
				))}
			</Radio.Group>
			{info && <RadioGroupInfo menus={info} />}
		</div>
	);
};

export default OWRadioGroup;
